// Bonsai Agents (BM-H620, 26 Sep 2026) — the Reviews page, built to the
// signed-off spec, renamed and re-topped per Vanessa's steer on 26 Sep:
// three agent buttons at the top of the page rather than agent cards buried
// inside the New review form, a smart matter search as the page's real entry
// point (reusing the same SmartClientMatterSearch used on attendance notes
// and Post Hub, not a bespoke search box), and document sources that reuse
// the matter's own SharePoint folder rather than any standing "browse
// everything" access — see the "from another matter" picker below, which is
// the whole answer to the templates-tool question: nothing is browsed until
// a fee earner has searched for and picked a specific matter first.
//
// Screens: list (this page's default view), new review, and a review's own
// detail (which is the running view and the result view — the same screen,
// because it is driven by one job's own record and just shows different
// parts of it depending on state). Nothing here is guessed: every field
// comes from GET /api/reviews/:id, which itself is a thin wrapper over
// hub.mate_review_agent's already-proven job record.
import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../config/api";
import SmartClientMatterSearch from "../components/SmartClientMatterSearch";
import MarkdownDoc from "../components/MarkdownDoc";
import Article4QuestionnairePanel from "../components/Article4QuestionnairePanel";
import { parseBlobError } from "../utils/parseBlobError";
import {
  FileText, Scale, Home, Upload, X, Loader2,
  ExternalLink, Copy, Folder, File as FileIcon, ChevronRight, Mail,
  AlertTriangle, ChevronLeft, Clock, Paperclip, ClipboardList, Landmark,
  Bot, ArrowRight, Hourglass, Hand, CheckCircle2, Plus, Sparkles,
  Send, MessageSquare, RotateCcw,
} from "lucide-react";

// Mirrors backend/hub/routes_reviews.py REVIEW_PAGE_MAX_FILES — kept in sync
// by hand since the two never need to change independently in practice.
const REVIEW_PAGE_MAX_FILES = 50;

// Mirrors backend/hub/mate_review_agent.py MAX_TURN_MINUTES — the point past
// which a running review is auto-stopped as stalled. There is no per-step
// progress signal from the agent to build a real ETA from, so the running
// screen's progress bar is honestly this: how close the review is to that
// ceiling, not a prediction of when it will actually finish.
const REVIEW_MAX_TURN_MINUTES = 90;

// hue: each agent's own two-stop gradient on the list page, so the five read
// as distinct helpers at a glance. Deliberately no green (green = status).
const AGENTS = [
  { key: "contract_review", label: "Contract Review", icon: FileText, hue: ["#3b82f6", "#6366f1"],
    blurb: "Reviews a contract or bundle you give it." },
  { key: "litigation", label: "Litigation", icon: Scale, hue: ["#ec4899", "#d946ef"],
    blurb: "CPR, insolvency procedure, strategy and drafting." },
  { key: "conveyancing", label: "Conveyancing", icon: Home, hue: ["#06b6d4", "#0ea5e9"],
    blurb: "File review: enquiries, mortgage offers, title, AML, SoF." },
  // BM-H3680: works from one matter's submitted questionnaire, no documents.
  { key: "epr", label: "Estate Planning Review", icon: ClipboardList, hue: ["#f97316", "#f43f5e"],
    blurb: "Drafts the fee earner note and client letter from the client's submitted questionnaire." },
  // Article 4 initial review: one matter's submitted questionnaire, and the client's uploads if you allow it.
  { key: "article4", label: "Article 4 Review", icon: Landmark, hue: ["#8b5cf6", "#a855f7"],
    blurb: "Deadlines, eligibility, route and costs from the client's Article 4 questionnaire and papers." },
];

const CONVEYANCING_TASKS = [
  "Enquiries review", "Mortgage offer summary", "Report on title",
  "Remortgage completion checklist", "AML/ID summary", "Source of funds review",
];

const STATE_LABELS = {
  running: "Running", needs_you: "Needs you", ready: "Ready",
  failed: "Failed", closed: "Closed",
};

function agentLabel(key) {
  return AGENTS.find((a) => a.key === key)?.label || key;
}

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return String(iso);
  }
}

// A numbered section label on the New review form, numbered in the agent's colours.
function StepLabel({ n, hue, children }) {
  return (
    <label className="flex items-center gap-2 text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">
      <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] text-white"
        style={{ background: `linear-gradient(135deg, ${hue[0]}, ${hue[1]})` }}>{n}</span>
      {children}
    </label>
  );
}

function StateBadge({ state }) {
  const cls = {
    running: "bg-teal/15 text-teal",
    needs_you: "bg-amber-500/15 text-amber-400",
    ready: "bg-emerald-500/15 text-emerald-400",
    failed: "bg-status-red-dim/30 text-status-red",
    closed: "bg-slate-700/40 text-slate-400",
  }[state] || "bg-slate-700/40 text-slate-400";
  return (
    <span className={`px-2 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ${cls}`}>
      {STATE_LABELS[state] || state}
    </span>
  );
}

export default function BonsaiAgents() {
  const [params, setParams] = useSearchParams();
  const jobId = params.get("job");
  const isNew = params.get("new") === "1";

  if (jobId) return <ReviewDetail jobId={jobId} onBack={() => setParams({})} onOpen={(id) => setParams({ job: id })} />;
  if (isNew) return <NewReview
    initialAgent={params.get("agent") || ""}
    initialMatter={params.get("matter") || ""}
    initialClient={params.get("client") || ""}
    initialTask={params.get("task") || ""}
    onBack={() => setParams({})}
    onStarted={(id) => setParams({ job: id })}
  />;
  return <ReviewsList
    onNew={(agent, matter) => setParams(matter
      ? { new: "1", agent, matter: matter.matter_reference || "", client: matter.client_name || "" }
      : { new: "1", agent })}
    onOpen={(id) => setParams({ job: id })}
  />;
}

// ---------------------------------------------------------------------------
// Screen 1: list.
// ---------------------------------------------------------------------------

function ReviewsList({ onNew, onOpen }) {
  const [rows, setRows] = useState([]);
  const [isDirector, setIsDirector] = useState(false);
  const [mineOnly, setMineOnly] = useState(true);
  const [stateFilter, setStateFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pickedMatter, setPickedMatter] = useState(null);
  const [archiving, setArchiving] = useState({}); // job_id -> true while in flight

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/reviews`, {
        params: { mine_only: mineOnly, include_archived: showArchived },
      });
      setRows(r.data.reviews || []);
      setIsDirector(!!r.data.is_director);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load reviews.");
    } finally {
      setLoading(false);
    }
  }, [mineOnly, showArchived]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const visible = stateFilter ? rows.filter((r) => r.state === stateFilter) : rows;

  const toggleArchive = async (e, jobId, archived) => {
    e.stopPropagation(); // don't also trigger the row's onOpen
    setArchiving((prev) => ({ ...prev, [jobId]: true }));
    try {
      if (archived) {
        await axios.post(`${API}/reviews/${jobId}/unarchive`);
      } else {
        await axios.post(`${API}/reviews/${jobId}/archive`);
      }
      await load();
    } finally {
      setArchiving((prev) => ({ ...prev, [jobId]: false }));
    }
  };

  // Live tally for the header chips: what is working, what needs a person,
  // what is waiting to be collected. Counted from the rows already loaded.
  const tally = rows.reduce((acc, r) => {
    if (!r.archived) acc[r.state] = (acc[r.state] || 0) + 1;
    return acc;
  }, {});
  const TALLY = [
    { state: "running", label: "running", icon: Loader2, spin: true,
      on: "bg-indigo-500/20 text-indigo-200 border-indigo-300/40" },
    { state: "needs_you", label: "need you", icon: Hand,
      on: "bg-amber-500/15 text-amber-300 border-amber-400/40" },
    { state: "ready", label: "ready", icon: CheckCircle2,
      on: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40" },
  ];
  const matterRef = pickedMatter?.matter_reference;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Hero: the AI team, what is in flight, and the matter search. */}
      <div className="agents-hero relative overflow-hidden rounded-2xl border border-white/10 p-6 mb-6 shadow-xl shadow-indigo-950/40">
        <div aria-hidden className="agents-orb absolute -top-28 -right-20 w-80 h-80 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(236,72,153,0.35), transparent 65%)" }} />
        <div aria-hidden className="agents-orb agents-orb-slow absolute -bottom-32 left-1/3 w-96 h-96 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(14,165,233,0.25), transparent 65%)" }} />

        <div className="relative flex flex-wrap items-start justify-between gap-4 mb-5">
          <div className="flex items-center gap-4">
            <div className="relative w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg shadow-fuchsia-900/40 ring-1 ring-white/20"
              style={{ background: "linear-gradient(135deg, #6366f1, #a855f7 55%, #ec4899)" }}>
              <Bot size={28} className="text-white" />
              <Sparkles size={16} className="agents-twinkle absolute -top-1.5 -right-1.5 text-amber-200" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold agents-title">Bonsai Agents</h1>
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-white/90 bg-white/10 ring-1 ring-white/20">
                  <Sparkles size={10} /> AI
                </span>
              </div>
              <p className="text-sm text-indigo-100/80 mt-0.5">
                Your AI specialist team. Pick a matter, choose a helper, collect a finished review.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-testid="bonsai-agents-tally">
            {TALLY.map(({ state, label, icon: Icon, spin, on }) => {
              const n = tally[state] || 0;
              const active = stateFilter === state;
              return (
                <button
                  key={state}
                  onClick={() => setStateFilter(active ? "" : state)}
                  title={n ? `Show only ${STATE_LABELS[state].toLowerCase()}` : undefined}
                  disabled={!n && !active}
                  className={`flex items-center gap-1.5 px-2.5 py-1 border text-xs font-medium backdrop-blur-sm transition-colors ${
                    n ? on : "border-white/10 text-indigo-100/40 bg-white/[0.03]"
                  } ${active ? "ring-2 ring-white/50" : ""} disabled:cursor-default`}
                  style={{ borderRadius: 9999 }}
                >
                  <Icon size={12} className={n && spin ? "animate-spin" : ""} />
                  <span className="tabular-nums">{n}</span> {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Smart search — the real entry point: find the matter you want to work on. */}
        <div className="relative max-w-xl">
          <SmartClientMatterSearch
            label="Find a matter"
            placeholder="Search clients or matters..."
            mattersOnly
            onSelect={(r) => setPickedMatter(r?.matter_reference ? r : null)}
          />
        </div>
      </div>

      {/* Agent cards — pick one to start work on the matter just found. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {AGENTS.map((a) => {
          const Icon = a.icon;
          const [c1, c2] = a.hue;
          return (
            <div
              key={a.key}
              className="agent-card group relative rounded-2xl overflow-hidden border border-white/10 bg-[#0f1830] transition-all duration-200 hover:-translate-y-1"
              style={{ "--c1": c1, "--c2": c2 }}
            >
              {/* colour wash in the agent's own hue */}
              <div aria-hidden className="absolute inset-0 opacity-60 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: `radial-gradient(120% 90% at 0% 0%, ${c1}38, transparent 60%), radial-gradient(90% 80% at 100% 100%, ${c2}24, transparent 60%)` }} />
              <div aria-hidden className="absolute inset-x-0 top-0 h-1" style={{ background: `linear-gradient(90deg, ${c1}, ${c2})` }} />
              <button
                onClick={() => onNew(a.key, pickedMatter)}
                data-testid={`bonsai-agent-button-${a.key}`}
                className="relative w-full h-full flex flex-col text-left p-5 bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              >
                <div className="flex items-start gap-4">
                  <div className="relative shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ring-1 ring-white/25 transition-transform duration-200 group-hover:scale-110 group-hover:rotate-3"
                    style={{ background: `linear-gradient(135deg, ${c1}, ${c2})`, boxShadow: `0 8px 24px -6px ${c1}aa` }}>
                    <Icon size={22} className="text-white" />
                    <Sparkles size={12} className="agents-twinkle absolute -top-1 -right-1 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-white leading-tight">{a.label}</span>
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{ color: "#fff", background: `${c1}55` }}>AI agent</span>
                    </div>
                    <p className="text-xs text-slate-300/90 mt-1.5 leading-relaxed">{a.blurb}</p>
                  </div>
                </div>
                <div className="mt-auto pt-4 flex items-center justify-between text-xs font-semibold">
                  <span className="agent-cta truncate">{matterRef ? `Start on ${matterRef}` : "Start a review"}</span>
                  <span className="w-7 h-7 rounded-full flex items-center justify-center text-white transition-transform duration-200 group-hover:translate-x-1"
                    style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
                    <ArrowRight size={14} />
                  </span>
                </div>
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isDirector && (
            <div className="flex rounded-md overflow-hidden border border-slate-700/50 text-xs">
              <button
                onClick={() => setMineOnly(true)}
                className={`px-3 py-1.5 ${mineOnly ? "bg-teal/15 text-teal" : "text-slate-400 hover:bg-slate-800"}`}
              >
                Mine
              </button>
              <button
                onClick={() => setMineOnly(false)}
                className={`px-3 py-1.5 ${!mineOnly ? "bg-teal/15 text-teal" : "text-slate-400 hover:bg-slate-800"}`}
              >
                Everyone's
              </button>
            </div>
          )}
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="bg-transparent border border-slate-700/50 rounded-md text-xs px-2 py-1.5 text-slate-300"
          >
            <option value="">All states</option>
            {Object.entries(STATE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-400 px-1">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Show archived
          </label>
        </div>
        <button onClick={() => onNew("", pickedMatter)} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-brand text-white hover:bg-brand-hover">
          <Plus size={14} /> New review
        </button>
      </div>

      {error && <div className="text-status-red text-sm mb-3">{error}</div>}
      {loading ? (
        <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading...</div>
      ) : visible.length === 0 ? (
        <div className="text-slate-500 text-sm py-10 text-center border border-dashed border-slate-700/50 rounded-xl flex flex-col items-center gap-2">
          <Hourglass size={20} className="text-slate-600" />
          No reviews yet. Search for a matter above, then pick an agent.
        </div>
      ) : (
        <div className="border border-slate-700/50 rounded-xl overflow-hidden bg-white/[0.015]">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02] text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Matter</th>
                <th className="text-left px-4 py-2 font-medium">Agent</th>
                <th className="text-left px-4 py-2 font-medium">Task</th>
                <th className="text-left px-4 py-2 font-medium">Started</th>
                <th className="text-left px-4 py-2 font-medium">State</th>
                <th className="text-left px-4 py-2 font-medium">Last activity</th>
                <th className="text-right px-4 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr
                  key={r.job_id}
                  onClick={() => onOpen(r.job_id)}
                  data-testid={`review-row-${r.job_id}`}
                  className={`border-t border-slate-700/30 cursor-pointer hover:bg-white/[0.03] ${r.state === "needs_you" ? "bg-amber-500/[0.06]" : ""} ${r.archived ? "opacity-50" : ""}`}
                >
                  <td className="px-4 py-2.5 text-slate-200">
                    {r.matter_reference}{r.client_name ? `, ${r.client_name}` : ""}
                  </td>
                  <td className="px-4 py-2.5 text-slate-300">{r.agent_label}</td>
                  <td className="px-4 py-2.5 text-slate-300">{r.task_label}</td>
                  <td className="px-4 py-2.5 text-slate-400 text-xs">
                    {fmtWhen(r.started_at)}{r.started_by_name ? ` · ${r.started_by_name}` : ""}
                  </td>
                  <td className="px-4 py-2.5"><StateBadge state={r.state} /></td>
                  <td className="px-4 py-2.5 text-slate-400 text-xs">{r.last_activity}</td>
                  <td className="px-4 py-2.5 text-right">
                    {r.state !== "running" && (
                      <button
                        onClick={(e) => toggleArchive(e, r.job_id, r.archived)}
                        disabled={!!archiving[r.job_id]}
                        className="text-xs text-slate-500 hover:text-slate-300 disabled:opacity-40"
                      >
                        {r.archived ? "Unarchive" : "Archive"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// A SharePoint matter-folder browser, reused for two different document
// sources: "from the matter folder" (the review's own matter) and "from
// another matter" (a separately searched one). Nothing is browsed until a
// matter_reference has been picked — either the review's own, or one found
// via its own SmartClientMatterSearch below. No standing access to anything.
// ---------------------------------------------------------------------------

function MatterFolderBrowser({ matterReference, addedKeys, onAdd, remainingSlots }) {
  const [path, setPath] = useState([]); // array of subfolder segment names
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [addAllNote, setAddAllNote] = useState("");

  useEffect(() => {
    if (!matterReference) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setAddAllNote("");
    axios.post(`${API}/reviews/browse-matter-files`, {
      matter_reference: matterReference, subfolder: path.join("/"),
    }).then((r) => {
      if (cancelled) return;
      if (r.data.error) setError(r.data.error);
      else setListing(r.data);
    }).catch((e) => {
      if (!cancelled) setError(e?.response?.data?.detail || "Could not browse this folder.");
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [matterReference, path]);

  if (!matterReference) return null;

  // Whole-folder add (Vanessa's request, 26 Sep 2026): the current folder
  // level only — never recurses into subfolders, so picking a folder never
  // silently pulls in a nested one the user didn't look at. Same 50-file
  // review cap as everywhere else; if the folder has more addable files than
  // there's room for, it adds what fits and says so, rather than either
  // failing the whole action or going over the limit unannounced.
  const addableInFolder = (listing?.files || []).filter(
    (f) => f.drive_id && f.item_id && !addedKeys.has(`${f.drive_id}:${f.item_id}`)
  );

  const addWholeFolder = () => {
    const toAdd = addableInFolder.slice(0, remainingSlots);
    toAdd.forEach((f) => onAdd({
      source: "sharepoint", drive_id: f.drive_id, item_id: f.item_id, filename: f.name,
    }));
    setAddAllNote(
      toAdd.length < addableInFolder.length
        ? `Added ${toAdd.length} of ${addableInFolder.length} — this review's ${REVIEW_PAGE_MAX_FILES}-file limit was reached.`
        : `Added all ${toAdd.length} file${toAdd.length === 1 ? "" : "s"} from this folder.`
    );
  };

  return (
    <div className="border border-slate-700/50 rounded-lg p-3 mt-2">
      <div className="flex items-center gap-1 text-xs text-slate-400 mb-2 flex-wrap">
        <button onClick={() => setPath([])} className="hover:text-teal">{matterReference}</button>
        {path.map((seg, i) => (
          <span key={i} className="flex items-center gap-1">
            <ChevronRight size={10} />
            <button onClick={() => setPath(path.slice(0, i + 1))} className="hover:text-teal">{seg}</button>
          </span>
        ))}
      </div>
      {loading && <div className="text-xs text-slate-500 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Loading...</div>}
      {error && <div className="text-xs text-status-red">{error}</div>}
      {listing && !loading && addableInFolder.length > 0 && (
        <div className="flex items-center justify-between mb-1.5">
          <button
            onClick={addWholeFolder}
            disabled={remainingSlots <= 0}
            className="text-[11px] text-teal hover:bg-teal/15 rounded px-2 py-0.5 disabled:text-slate-500 disabled:hover:bg-transparent"
          >
            {remainingSlots <= 0
              ? `This review already has the most files allowed (${REVIEW_PAGE_MAX_FILES})`
              : `Add all ${addableInFolder.length} file${addableInFolder.length === 1 ? "" : "s"} in this folder`}
          </button>
        </div>
      )}
      {addAllNote && <div className="text-[11px] text-slate-400 mb-1.5">{addAllNote}</div>}
      {listing && !loading && (
        <ul className="space-y-0.5 max-h-56 overflow-y-auto">
          {listing.folders?.map((f) => (
            <li key={f.name}>
              <button
                onClick={() => setPath([...path, f.name])}
                className="w-full flex items-center gap-2 text-left text-xs px-2 py-1.5 rounded hover:bg-white/[0.05] text-slate-300"
              >
                <Folder size={13} className="text-slate-500 shrink-0" /> {f.name}
              </button>
            </li>
          ))}
          {listing.files?.map((f) => {
            const key = `${f.drive_id}:${f.item_id}`;
            const added = addedKeys.has(key);
            return (
              <li key={key} className="flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-white/[0.05]">
                <span className="flex items-center gap-2 text-slate-300 truncate">
                  <FileIcon size={13} className="text-slate-500 shrink-0" /> {f.name}
                </span>
                <button
                  disabled={added || !f.drive_id || !f.item_id}
                  onClick={() => onAdd({
                    source: "sharepoint", drive_id: f.drive_id, item_id: f.item_id, filename: f.name,
                  })}
                  className={`ml-2 shrink-0 px-2 py-0.5 rounded text-[11px] ${
                    added ? "text-emerald-400" : "text-teal hover:bg-teal/15"
                  }`}
                >
                  {added ? "Added" : "Add"}
                </button>
              </li>
            );
          })}
          {!listing.folders?.length && !listing.files?.length && (
            <li className="text-xs text-slate-500 px-2 py-1">Empty folder.</li>
          )}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Screen 2: new review.
// ---------------------------------------------------------------------------

function NewReview({ initialAgent, initialMatter, initialClient, initialTask, onBack, onStarted }) {
  const [batchId] = useState(() => `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [agent, setAgent] = useState(initialAgent || "");
  // Arriving from an agent card goes straight into that agent's form; the
  // picker only shows when no agent came in, or after "Switch agent".
  const [choosing, setChoosing] = useState(!AGENTS.some((x) => x.key === initialAgent));
  const [matter, setMatter] = useState(initialMatter
    ? { matter_reference: initialMatter, client_name: initialClient || "" } : null);
  // BM-H4384: a residential checklist opens this screen with the task picked.
  const [task, setTask] = useState(
    (initialAgent === "conveyancing" && CONVEYANCING_TASKS.includes(initialTask)) ? initialTask : "");
  const [instructions, setInstructions] = useState("");
  const [readAccess, setReadAccess] = useState(true);
  // Article 4: reading the client's uploads is the fee earner's explicit choice each time (BM-H3691), so it starts unticked.
  const [a4Uploads, setA4Uploads] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState([]); // {filename, size, accepted, reason}
  const [uploading, setUploading] = useState(false);
  const [saveToMatter, setSaveToMatter] = useState(false);
  const [saveSubfolder, setSaveSubfolder] = useState("");
  const [conflictChecked, setConflictChecked] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState("");
  const fileInputRef = useRef(null);

  // Documents added via the matter-folder / other-matter / email pickers.
  const [pickedDocs, setPickedDocs] = useState([]); // {source, drive_id, item_id, filename} | {source:'email_attachment', message_id, attachment_id, filename}
  const [showOwnFolder, setShowOwnFolder] = useState(false);
  const [otherMatter, setOtherMatter] = useState(null);
  const [showEmailPicker, setShowEmailPicker] = useState(false);
  const [emailQuery, setEmailQuery] = useState("");
  const [emailResults, setEmailResults] = useState([]);
  const [emailSearching, setEmailSearching] = useState(false);
  const [openEmail, setOpenEmail] = useState(null); // {id, subject, attachments}
  const [emailLoading, setEmailLoading] = useState(false);

  const addedKeys = new Set(pickedDocs.map((d) =>
    d.source === "sharepoint" ? `${d.drive_id}:${d.item_id}` : `${d.message_id}:${d.attachment_id}`));

  const acceptedFiles = droppedFiles.filter((f) => f.accepted);
  const totalBytes = acceptedFiles.reduce((s, f) => s + (f.size || 0), 0);
  const totalDocCount = acceptedFiles.length + pickedDocs.length;

  const handleFiles = async (fileList) => {
    setUploading(true);
    for (const file of Array.from(fileList)) {
      const form = new FormData();
      form.append("file", file);
      try {
        const r = await axios.post(`${API}/reviews/drafts/${batchId}/uploads`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setDroppedFiles((prev) => [...prev, r.data]);
      } catch (e) {
        setDroppedFiles((prev) => [...prev, {
          accepted: false, filename: file.name,
          reason: e?.response?.data?.detail || "upload failed",
        }]);
      }
    }
    setUploading(false);
  };

  const removeFile = (filename) => {
    setDroppedFiles((prev) => prev.filter((f) => f.filename !== filename));
    // Note: does not un-stash server side; the stash expires on its own
    // (24h TTL) and is never sent to the agent unless still listed here.
  };

  const removePickedDoc = (doc) => {
    setPickedDocs((prev) => prev.filter((d) => d !== doc));
  };

  const searchEmails = async (q) => {
    setEmailQuery(q);
    if (q.trim().length < 2) { setEmailResults([]); return; }
    setEmailSearching(true);
    try {
      const r = await axios.post(`${API}/reviews/search-emails`, { query: q, folder: "all" });
      setEmailResults(r.data.emails || []);
    } catch {
      setEmailResults([]);
    } finally {
      setEmailSearching(false);
    }
  };

  const openEmailToPick = async (id) => {
    setEmailLoading(true);
    try {
      const r = await axios.post(`${API}/reviews/read-email`, { message_id: id });
      setOpenEmail(r.data);
    } catch {
      setOpenEmail({ id, subject: "(could not open)", attachments: [] });
    } finally {
      setEmailLoading(false);
    }
  };

  const isEpr = agent === "epr";
  const isA4 = agent === "article4";
  // Both work from one matter's submitted questionnaire, not from documents you give them.
  const isQuestionnaire = isEpr || isA4;
  const needsDocuments = agent === "contract_review" || (agent === "conveyancing" && !readAccess);
  const needsConflictCheck = agent === "litigation";
  // BM-H4384: a picked conveyancing task is an instruction in itself; the
  // backend names its skill for the agent (hub/mate_review_agent.py).
  const taskIsInstruction = agent === "conveyancing" && !!task;
  const canStart = agent && matter?.matter_reference && (isQuestionnaire || taskIsInstruction || instructions.trim())
    && (!needsDocuments || totalDocCount > 0) && (!needsConflictCheck || conflictChecked) && !starting;

  const checkLine = () => {
    if (isA4) {
      return `The Article 4 review agent will read ${matter?.matter_reference}'s submitted questionnaire through CaseMinder, as you`
        + (a4Uploads ? ", and the papers the client uploaded with it." : ". It will not read the client's uploads.")
        + " It will not read your email or the matter folder.";
    }
    if (isEpr) {
      return `The Estate Planning Review agent will read ${matter?.matter_reference}'s submitted questionnaire through CaseMinder, as you. It will not read your email or the matter folder.`;
    }
    const n = totalDocCount;
    const mb = (totalBytes / (1024 * 1024)).toFixed(1);
    const who = agentLabel(agent) || "the agent";
    const parts = [`Sending ${n} document${n === 1 ? "" : "s"}${acceptedFiles.length ? ` (${mb} MB)` : ""} to the ${who} agent`];
    if (task) parts.push(`for ${task.toLowerCase()}`);
    if (matter?.matter_reference) parts.push(`on ${matter.matter_reference}`);
    let line = parts.join(" ") + ".";
    if (readAccess) line += " It may also read the matter folder and your email.";
    return line;
  };

  const handleStart = async () => {
    setStarting(true);
    setStartError("");
    const documents = isQuestionnaire ? [] : [
      ...acceptedFiles.map((f) => ({ source: "paperclip", filename: f.filename })),
      ...pickedDocs,
    ];
    // Litigation's own set-up (Vanessa's decision): the conflict check tick
    // is passed to the agent in its instructions so it does not stop the
    // review to ask. Sent this way rather than as a separate field so
    // nothing on the backend needs to know about a per-agent quirk.
    const effectiveInstructions = needsConflictCheck
      ? `The solicitor has confirmed the conflict check for this matter is complete. Do not ask about it.\n\n${instructions}`
      : instructions;
    try {
      const r = await axios.post(`${API}/reviews`, {
        matter_reference: matter.matter_reference,
        client_name: matter.client_name || matter.client || "",
        acting_for: matter.acting_for || "",
        agent,
        task_label: task || instructions.split("\n")[0].slice(0, 120),
        instructions: effectiveInstructions,
        documents,
        draft_batch_id: batchId,
        // For the Article 4 agent the tick means only "may read the client's uploads".
        read_access: isA4 ? a4Uploads : isEpr ? false : readAccess,
        save_dropped_to_matter: isQuestionnaire ? false : saveToMatter,
        save_subfolder: saveSubfolder,
      });
      if (!r.data.started) {
        setStartError(r.data.error || "Could not start the review.");
        setStarting(false);
        return;
      }
      onStarted(r.data.job_id);
    } catch (e) {
      setStartError(e?.response?.data?.detail || "Could not start the review.");
      setStarting(false);
    }
  };

  // The picked agent's own colours carry through the form (header, step
  // numbers, drop zone, Start button); indigo-to-violet before one is picked.
  const sel = AGENTS.find((x) => x.key === agent);
  const [h1, h2] = sel?.hue || ["#6366f1", "#a855f7"];
  const SelIcon = sel?.icon || Bot;
  const nMatter = choosing ? 2 : 1;
  const nInstr = nMatter + (agent === "conveyancing" ? 2 : 1);
  const Step = ({ n, children }) => <StepLabel n={n} hue={[h1, h2]}>{children}</StepLabel>;

  return (
    <div className="p-6 max-w-3xl mx-auto" style={{ "--c1": h1, "--c2": h2 }}>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200 mb-4">
        <ChevronLeft size={16} /> Back to Bonsai Agents
      </button>

      {/* Header in the picked agent's colours */}
      <div className="relative overflow-hidden rounded-2xl border border-white/10 p-5 mb-6 transition-colors duration-300"
        style={{ background: `linear-gradient(120deg, ${h1}40, #1e1b4b 55%, ${h2}30)` }}>
        <div aria-hidden className="agents-orb absolute -top-20 -right-16 w-64 h-64 rounded-full"
          style={{ background: `radial-gradient(circle, ${h2}55, transparent 65%)` }} />
        <div className="relative flex items-center gap-4">
          <div className="relative w-12 h-12 rounded-xl flex items-center justify-center ring-1 ring-white/25 transition-colors duration-300"
            style={{ background: `linear-gradient(135deg, ${h1}, ${h2})`, boxShadow: `0 8px 24px -6px ${h1}` }}>
            <SelIcon size={24} className="text-white" />
            <Sparkles size={14} className="agents-twinkle absolute -top-1.5 -right-1.5 text-amber-200" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold agents-title">New review</h1>
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-white/90 bg-white/10 ring-1 ring-white/20">
                <Sparkles size={10} /> AI
              </span>
            </div>
            {sel && !choosing ? (
              <div className="mt-0.5">
                <p className="text-sm font-semibold text-white">{sel.label}</p>
                <p className="text-xs text-indigo-100/70">{sel.blurb}</p>
              </div>
            ) : (
              <p className="text-sm text-indigo-100/80 mt-0.5">Pick an AI specialist, point it at a matter, and brief it.</p>
            )}
          </div>
          {sel && !choosing && (
            <button
              onClick={() => setChoosing(true)}
              className="ml-auto shrink-0 text-xs px-2.5 py-1 text-white/80 hover:text-white bg-white/10 hover:bg-white/15 ring-1 ring-white/20"
            >
              Switch agent
            </button>
          )}
        </div>
      </div>

      {/* Agent picker: only when none came in from a card, or on "Switch agent". */}
      {choosing && (
      <div className="mb-6">
        <Step n={1}>Agent</Step>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {AGENTS.map((a) => {
            const Icon = a.icon;
            const active = agent === a.key;
            const [c1, c2] = a.hue;
            return (
              <div
                key={a.key}
                className={`agent-card group relative rounded-xl overflow-hidden border bg-[#0f1830] transition-all duration-200 ${
                  active ? "-translate-y-0.5" : "border-white/10 hover:-translate-y-0.5"
                } ${agent && !active ? "opacity-60 hover:opacity-100" : ""}`}
                style={{ "--c1": c1, "--c2": c2, ...(active ? { borderColor: c1, boxShadow: `0 14px 32px -14px ${c1}` } : {}) }}
              >
                <div aria-hidden className={`absolute inset-0 transition-opacity duration-300 ${active ? "opacity-100" : "opacity-40 group-hover:opacity-80"}`}
                  style={{ background: `radial-gradient(120% 90% at 0% 0%, ${c1}38, transparent 60%)` }} />
                {active && <div aria-hidden className="absolute inset-x-0 top-0 h-1" style={{ background: `linear-gradient(90deg, ${c1}, ${c2})` }} />}
                <button
                  onClick={() => { setAgent(a.key); setChoosing(false); }}
                  aria-pressed={active}
                  className="relative w-full h-full text-left p-3 bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                >
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ring-1 ring-white/20 transition-transform duration-200 group-hover:scale-105"
                      style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
                      <Icon size={18} className="text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-white leading-tight">{a.label}</span>
                      <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">{a.blurb}</p>
                    </div>
                    {active && (
                      <span className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-white"
                        style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}>
                        <CheckCircle2 size={13} />
                      </span>
                    )}
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Matter */}
      <div className="mb-6">
        <Step n={nMatter}>Matter</Step>
        <SmartClientMatterSearch
          label=""
          placeholder="Type a reference or client name"
          mattersOnly
          initialValue={matter?.matter_reference || ""}
          onSelect={(r) => setMatter(r?.matter_reference ? r : null)}
        />
        {matter?.matter_reference && (
          <div className="mt-1 text-xs text-emerald-400">Selected: {matter.matter_reference}{matter.client_name ? ` — ${matter.client_name}` : ""}</div>
        )}
      </div>

      {/* Task */}
      {agent === "conveyancing" && (
        <div className="mb-4">
          <Step n={nMatter + 1}>Task</Step>
          <select
            value={task}
            onChange={(e) => setTask(e.target.value)}
            className="w-full px-3 py-2 bg-white/[0.02] border border-slate-700/50 rounded-md text-sm text-slate-100"
          >
            <option value="">Free text (describe below)</option>
            {CONVEYANCING_TASKS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      )}
      <div className="mb-6">
        <Step n={nInstr}>Instructions</Step>
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          rows={4}
          placeholder={isQuestionnaire
            ? "Optional. Leave blank for the standard review: it will summarise first and file nothing until you say so."
            : taskIsInstruction
            ? `Optional. Leave blank and the agent runs its ${task.toLowerCase()} skill on the matter's documents.`
            : "Anything the agent should know: dates, what the client wants, what to focus on."}
          className="w-full px-3 py-2 bg-white/[0.02] border border-slate-700/50 rounded-md text-sm text-slate-100 placeholder:text-slate-500"
        />
      </div>

      {/* Documents (not for the Estate Planning Review: it works from the questionnaire alone) */}
      {!isQuestionnaire && (
      <div className="mb-6">
        <Step n={nInstr + 1}>Documents</Step>

        {/* Source 1: drag and drop */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
          onClick={() => fileInputRef.current?.click()}
          className="agent-drop border-2 border-dashed border-slate-700/50 rounded-xl py-8 text-center cursor-pointer transition-colors"
        >
          <span className="mx-auto mb-2 w-10 h-10 rounded-full flex items-center justify-center text-white"
            style={{ background: `linear-gradient(135deg, ${h1}, ${h2})` }}>
            <Upload size={18} />
          </span>
          <p className="text-sm text-slate-400">Drop files here, or click to choose</p>
          <input ref={fileInputRef} type="file" multiple className="hidden"
                 onChange={(e) => handleFiles(e.target.files)} />
        </div>
        {uploading && <div className="text-xs text-slate-500 mt-2 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Uploading...</div>}
        {droppedFiles.length > 0 && (
          <ul className="mt-2 space-y-1">
            {droppedFiles.map((f, i) => (
              <li key={`${f.filename}-${i}`} className={`flex items-center justify-between text-xs px-2 py-1.5 rounded ${f.accepted ? "bg-white/[0.02]" : "bg-status-red-dim/20"}`}>
                <span className={f.accepted ? "text-slate-300" : "text-status-red"}>
                  {f.filename}{f.accepted ? ` (${((f.size || 0) / 1024).toFixed(0)} KB)` : ` — ${f.reason}`}
                </span>
                <button onClick={() => removeFile(f.filename)} className="text-slate-500 hover:text-slate-300"><X size={12} /></button>
              </li>
            ))}
          </ul>
        )}
        {acceptedFiles.length > 0 && (
          <label className="flex items-start gap-2 mt-3 text-xs text-slate-400">
            <input type="checkbox" checked={saveToMatter} onChange={(e) => setSaveToMatter(e.target.checked)} className="mt-0.5" />
            <span>
              Also save dropped files to the matter folder
              {saveToMatter && (
                <input
                  value={saveSubfolder}
                  onChange={(e) => setSaveSubfolder(e.target.value)}
                  placeholder="Subfolder (e.g. Enquiries)"
                  className="ml-2 px-2 py-0.5 bg-white/[0.02] border border-slate-700/50 rounded text-xs text-slate-100"
                />
              )}
            </span>
          </label>
        )}

        {/* Picked documents (from the matter folder / another matter / email) */}
        {pickedDocs.length > 0 && (
          <ul className="mt-3 space-y-1">
            {pickedDocs.map((d, i) => (
              <li key={i} className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-white/[0.02] text-slate-300">
                <span className="flex items-center gap-2 truncate">
                  <FileIcon size={12} className="text-slate-500 shrink-0" /> {d.filename}
                  <span className="text-slate-500">
                    {d.source === "sharepoint" ? "— matter folder" : "— email attachment"}
                  </span>
                </span>
                <button onClick={() => removePickedDoc(d)} className="text-slate-500 hover:text-slate-300 shrink-0"><X size={12} /></button>
              </li>
            ))}
          </ul>
        )}

        {/* Source 2: from the matter folder (the review's own matter). Needs a
            matter picked first (to know whose folder), which isn't obvious
            from its plain absence beforehand — Vanessa's live UAT read that
            as drag-and-drop and folder-picking being either/or. This hint
            fills the gap it otherwise leaves. */}
        {!matter?.matter_reference && (
          <p className="text-xs text-slate-500 mt-3">
            Pick a matter above to also browse its SharePoint folder here.
          </p>
        )}
        {matter?.matter_reference && (
          <div className="mt-3">
            <button
              onClick={() => setShowOwnFolder((s) => !s)}
              className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"
            >
              <Folder size={12} /> From the matter folder {showOwnFolder ? "▾" : "▸"}
            </button>
            {showOwnFolder && (
              <MatterFolderBrowser
                matterReference={matter.matter_reference}
                addedKeys={addedKeys}
                onAdd={(doc) => setPickedDocs((prev) => [...prev, doc])}
                remainingSlots={REVIEW_PAGE_MAX_FILES - totalDocCount}
              />
            )}
          </div>
        )}

        {/* Source 3: from another matter — search for a past matter, then
            browse only that one matter's folder. Nothing is browsed until a
            specific matter has been searched for and picked. */}
        <div className="mt-3">
          <p className="text-xs text-slate-400 mb-1 flex items-center gap-1"><Folder size={12} /> From another matter (e.g. reuse a past document as a starting point)</p>
          <SmartClientMatterSearch
            label=""
            placeholder="Search for the matter to pull a document from..."
            mattersOnly
            onSelect={(r) => setOtherMatter(r?.matter_reference ? r : null)}
          />
          {otherMatter?.matter_reference && (
            <MatterFolderBrowser
              matterReference={otherMatter.matter_reference}
              addedKeys={addedKeys}
              onAdd={(doc) => setPickedDocs((prev) => [...prev, doc])}
              remainingSlots={REVIEW_PAGE_MAX_FILES - totalDocCount}
            />
          )}
        </div>

        {/* Source 4: from email */}
        <div className="mt-3">
          <button
            onClick={() => setShowEmailPicker((s) => !s)}
            className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"
          >
            <Mail size={12} /> From email {showEmailPicker ? "▾" : "▸"}
          </button>
          {showEmailPicker && (
            <div className="border border-slate-700/50 rounded-lg p-3 mt-2">
              <input
                value={emailQuery}
                onChange={(e) => searchEmails(e.target.value)}
                placeholder="Search your mailbox..."
                className="w-full px-2 py-1.5 bg-white/[0.02] border border-slate-700/50 rounded text-xs text-slate-100 placeholder:text-slate-500 mb-2"
              />
              {emailSearching && <div className="text-xs text-slate-500 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Searching...</div>}
              {!openEmail && emailResults.length > 0 && (
                <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                  {emailResults.map((m) => (
                    <li key={m.id}>
                      <button
                        onClick={() => openEmailToPick(m.id)}
                        disabled={!m.has_attachments}
                        className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-white/[0.05] disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <div className="text-slate-200 truncate">{m.subject}</div>
                        <div className="text-slate-500">{m.from_name || m.from} · {m.received}{!m.has_attachments ? " · no attachments" : ""}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {emailLoading && <div className="text-xs text-slate-500 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Opening...</div>}
              {openEmail && (
                <div>
                  <button onClick={() => setOpenEmail(null)} className="text-xs text-slate-400 hover:text-slate-200 mb-1 flex items-center gap-1">
                    <ChevronLeft size={11} /> Back to results
                  </button>
                  <div className="text-xs text-slate-300 mb-1 truncate">{openEmail.subject}</div>
                  <ul className="space-y-0.5">
                    {(openEmail.attachments || []).map((a) => {
                      const key = `${openEmail.id}:${a.attachment_id}`;
                      const added = addedKeys.has(key);
                      return (
                        <li key={key} className="flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-white/[0.05]">
                          <span className="flex items-center gap-2 text-slate-300 truncate">
                            <FileIcon size={12} className="text-slate-500 shrink-0" /> {a.filename}
                          </span>
                          <button
                            disabled={added}
                            onClick={() => setPickedDocs((prev) => [...prev, {
                              source: "email_attachment", message_id: openEmail.id,
                              attachment_id: a.attachment_id, filename: a.filename,
                            }])}
                            className={`ml-2 shrink-0 px-2 py-0.5 rounded text-[11px] ${added ? "text-emerald-400" : "text-teal hover:bg-teal/15"}`}
                          >
                            {added ? "Added" : "Add"}
                          </button>
                        </li>
                      );
                    })}
                    {!openEmail.attachments?.length && <li className="text-xs text-slate-500 px-2 py-1">No attachments.</li>}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {isA4 && matter?.matter_reference && (
        <Article4QuestionnairePanel matterRef={matter.matter_reference}
          defaultEmail={matter.client_email || ""} defaultName={matter.client_name || ""} />
      )}

      {/* Read access */}
      {isA4 ? (
        <label className="flex items-start gap-2 mb-6 text-sm text-slate-300" data-testid="a4-uploads-permission">
          <input type="checkbox" checked={a4Uploads} onChange={(e) => setA4Uploads(e.target.checked)} className="mt-1" />
          <span>The agent may read the papers the client uploaded with their questionnaire. It cannot read your email
            or the matter folder, cannot send anything, and files a draft only after you tell it to.</span>
        </label>
      ) : isEpr ? (
        <p className="mb-6 text-sm text-slate-400" data-testid="epr-agent-note">
          This agent reads only the client's submitted questionnaire, through CaseMinder, as you. It cannot send anything,
          and it cannot read your email, the matter folder or any uploaded file. It files a draft to the matter's Drafts
          folder only after you tell it to.
        </p>
      ) : (
      <label className="flex items-start gap-2 mb-6 text-sm text-slate-300">
        <input type="checkbox" checked={readAccess} onChange={(e) => setReadAccess(e.target.checked)} className="mt-1" />
        <span>The agent may also read this matter's SharePoint folder and my email while it works (read only; it cannot send, move, delete or file anything).</span>
      </label>
      )}

      {/* Litigation's own set-up: required, so the agent is never left to
          stop the review and ask. Not yet showing the matter's real conflict
          status — this is a plain confirmation only. */}
      {needsConflictCheck && (
        <label className="flex items-start gap-2 mb-6 text-sm text-slate-300">
          <input type="checkbox" checked={conflictChecked} onChange={(e) => setConflictChecked(e.target.checked)} className="mt-1" />
          <span>Conflict check completed for this matter.</span>
        </label>
      )}

      {agent && matter?.matter_reference && (
        <div className="text-xs text-slate-500 mb-3 italic">{checkLine()}</div>
      )}
      {startError && <div className="text-status-red text-sm mb-3">{startError}</div>}

      <button
        onClick={handleStart}
        disabled={!canStart}
        className="agent-start w-full py-3 flex items-center justify-center gap-2 text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        style={{ background: `linear-gradient(90deg, ${h1}, ${h2})`, boxShadow: canStart ? `0 10px 28px -10px ${h1}` : "none" }}
      >
        {starting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
        {starting ? "Starting..." : "Start review"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The chat with the agent on a review (2 Nov 2026). Staff asked for "a
// conversation in the review". The follow-up box already existed, as a
// small "Carry on" form at the foot of the page, and it vanished when the
// review closed after 24 quiet hours. This puts the whole exchange near the
// top as a chat, and on a closed review offers "Pick up this review", which
// starts a new review with this conversation and the same documents.
// Only the person who started the review can write in it.
// ---------------------------------------------------------------------------

function ChatBubble({ entry, agentLabel }) {
  const mine = entry.role === "solicitor";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-lg px-3 py-2 ${
        mine ? "bg-indigo-500/15 border border-indigo-400/20" : "bg-white/[0.03] border border-slate-700/50"
      } ${entry.earlier ? "opacity-70" : ""}`}>
        <div className="text-[11px] text-slate-500 mb-0.5 flex items-center gap-1">
          {mine ? "You" : <><Bot size={11} /> {agentLabel}</>}
        </div>
        {mine ? (
          <div className="whitespace-pre-wrap text-sm text-slate-200">{entry.text}</div>
        ) : (
          <MarkdownDoc markdown={entry.text} className="text-sm" />
        )}
      </div>
    </div>
  );
}

function ReviewChat({
  data, replyText, setReplyText, attachedFiles, acceptedAttachments, attaching, sending, sendError,
  replyFileInputRef, attachReplyFiles, removeAttachedFile, onSend, onPickUp, onClose, onOpen,
}) {
  const convo = data.conversation || [];
  // Open at the newest message, like any chat, and follow new ones.
  const scrollRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [convo.length, data.state]);
  const isOwner = data.is_owner !== false;
  const isOpen = data.state === "ready";
  const isEnded = data.state === "closed" || data.state === "failed";
  const canPickUp = isOwner && isEnded && !data.picked_up_as;
  const canWrite = isOwner && (isOpen || canPickUp);
  const firstNew = convo.findIndex((e) => !e.earlier);
  const send = canPickUp ? onPickUp : onSend;
  const disabled = sending || (canPickUp
    ? !replyText.trim()
    : (!replyText.trim() && acceptedAttachments.length === 0));
  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && !disabled) {
      e.preventDefault();
      send();
    }
  };

  if (convo.length === 0 && !canWrite && data.state !== "running") return null;

  return (
    <div className="mb-6">
      <h2 className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
        <MessageSquare size={13} /> Chat with the agent
      </h2>

      {data.picked_up_from && (
        <p className="text-xs text-slate-500 mb-2">
          Carries on from{" "}
          <button onClick={() => onOpen?.(data.picked_up_from)} className="text-indigo-300 hover:underline">
            an earlier review
          </button>.
        </p>
      )}
      {data.missing_documents?.length > 0 && (
        <div className="text-xs text-amber-400 mb-2 flex items-start gap-1">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span>
            Could not be fetched again, so the agent does not have: {data.missing_documents.join(", ")}.
            Attach them below if it needs them.
          </span>
        </div>
      )}

      {convo.length > 0 && (
        <div ref={scrollRef} className="space-y-2 max-h-[32rem] overflow-y-auto pr-1 mb-3">
          {convo.map((entry, i) => (
            <div key={i}>
              {i === 0 && entry.earlier && (
                <div className="text-[11px] text-slate-500 text-center mb-2">Earlier review</div>
              )}
              {i === firstNew && firstNew > 0 && (
                <div className="flex items-center gap-2 text-[11px] text-slate-500 my-3">
                  <div className="flex-1 border-t border-slate-700/50" />
                  Picked up {fmtWhen(data.started_at)}
                  <div className="flex-1 border-t border-slate-700/50" />
                </div>
              )}
              <ChatBubble entry={entry} agentLabel={data.agent_label} />
            </div>
          ))}
          {data.state === "running" && (
            <div className="flex justify-start">
              <div className="rounded-lg px-3 py-2 bg-white/[0.03] border border-slate-700/50 text-sm text-slate-400 flex items-center gap-2">
                <Loader2 size={13} className="animate-spin" /> The agent is working on it...
              </div>
            </div>
          )}
        </div>
      )}

      {data.picked_up_as && (
        <button
          onClick={() => onOpen?.(data.picked_up_as)}
          className="flex items-center gap-1 text-sm text-indigo-300 hover:underline"
        >
          This review was picked up again. Carry on there <ArrowRight size={14} />
        </button>
      )}

      {canWrite && (
        <div className="border border-slate-700/50 rounded-lg p-2 bg-white/[0.02]">
          {canPickUp && (
            <p className="text-xs text-slate-400 mb-2 px-1">
              This review has closed. Ask your question and it will be picked up as a new review, with this
              conversation and the same documents fetched again.
            </p>
          )}
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={onKeyDown}
            rows={3}
            placeholder={canPickUp
              ? "Ask the agent a follow-up question."
              : "Ask the agent a question, answer its questions, or ask for a redraft."}
            className="w-full px-2 py-1.5 bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none resize-y"
          />
          {attachedFiles.length > 0 && (
            <ul className="mb-2 space-y-1">
              {attachedFiles.map((f, i) => (
                <li key={`${f.filename}-${i}`} className={`flex items-center justify-between text-xs px-2 py-1.5 rounded ${f.accepted === false ? "bg-status-red-dim/20" : "bg-white/[0.02]"}`}>
                  <span className={f.accepted === false ? "text-status-red" : "text-slate-300"}>
                    {f.filename}{f.accepted === false ? ` — ${f.reason}` : f.size ? ` (${(f.size / 1024).toFixed(0)} KB)` : ""}
                  </span>
                  <button onClick={() => removeAttachedFile(f.filename)} className="text-slate-500 hover:text-slate-300"><X size={12} /></button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <button
                onClick={() => replyFileInputRef.current?.click()}
                disabled={attaching}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200 disabled:opacity-40"
              >
                {attaching ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
                {attaching ? "Attaching..." : "Attach a document"}
              </button>
              <input
                ref={replyFileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => { attachReplyFiles(e.target.files); e.target.value = ""; }}
              />
              {isOpen && (
                <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-300">
                  Close review
                </button>
              )}
            </div>
            <button
              onClick={send}
              disabled={disabled}
              title="Ctrl+Enter to send"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-500/20 text-indigo-200 text-sm hover:bg-indigo-500/30 disabled:opacity-40"
            >
              {canPickUp ? <RotateCcw size={13} /> : <Send size={13} />}
              {sending ? (canPickUp ? "Picking up..." : "Sending...") : (canPickUp ? "Pick up this review" : "Send")}
            </button>
          </div>
          {sendError && <p className="text-status-red text-xs mt-2 px-1">{sendError}</p>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Screens 3 & 4: a review's own detail (running, then result).
// ---------------------------------------------------------------------------

function ReviewDetail({ jobId, onBack, onOpen }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [confirmStop, setConfirmStop] = useState(false);
  const [elapsed, setElapsed] = useState("");
  const [elapsedMins, setElapsedMins] = useState(0);
  // output_id -> "saving" | {ok:true, web_url} | {error}
  const [outputSaveState, setOutputSaveState] = useState({});
  // output_id -> "downloading" | {error} — see downloadOutput below.
  const [outputDownloadState, setOutputDownloadState] = useState({});
  // Documents attached to the next reply — {filename, size} once staged
  // server side against this job (see reply-uploads), or {filename, reason}
  // if the server rejected it (never dropped silently, per spec).
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [attaching, setAttaching] = useState(false);
  const replyFileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/reviews/${jobId}`);
      setData(r.data);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load this review.");
    }
  }, [jobId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!data?.started_at || data.state !== "running") { setElapsed(""); setElapsedMins(0); return; }
    const tick = () => {
      const mins = Math.max(0, Math.round((Date.now() - new Date(data.started_at).getTime()) / 60000));
      setElapsed(`running ${mins} min`);
      setElapsedMins(mins);
    };
    tick();
    const t = setInterval(tick, 15000);
    return () => clearInterval(t);
  }, [data?.started_at, data?.state]);

  if (error) return <div className="p-6 max-w-3xl mx-auto text-status-red text-sm">{error}</div>;
  if (!data) return <div className="p-6 max-w-3xl mx-auto text-slate-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading...</div>;

  const doStop = async () => {
    await axios.post(`${API}/reviews/${jobId}/stop`);
    setConfirmStop(false);
    load();
  };
  const doClose = async () => {
    await axios.post(`${API}/reviews/${jobId}/close`);
    load();
  };
  const attachReplyFiles = async (fileList) => {
    setAttaching(true);
    for (const file of Array.from(fileList)) {
      const form = new FormData();
      form.append("file", file);
      try {
        const r = await axios.post(`${API}/reviews/${jobId}/reply-uploads`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAttachedFiles((prev) => [...prev, r.data]);
      } catch (e) {
        setAttachedFiles((prev) => [...prev, {
          accepted: false, filename: file.name,
          reason: e?.response?.data?.detail || "upload failed",
        }]);
      }
    }
    setAttaching(false);
  };
  const removeAttachedFile = (filename) => {
    setAttachedFiles((prev) => prev.filter((f) => f.filename !== filename));
  };
  const acceptedAttachments = attachedFiles.filter((f) => f.accepted !== false);
  const doReply = async () => {
    if (!replyText.trim() && acceptedAttachments.length === 0) return;
    setSending(true);
    setSendError("");
    try {
      const r = await axios.post(`${API}/reviews/${jobId}/reply`, {
        message: replyText,
        attached_files: acceptedAttachments.map((f) => f.filename),
      });
      if (r?.data?.success === false) {
        setSendError(r.data.error || "The agent could not take that message.");
        return;
      }
      setReplyText("");
      setAttachedFiles([]);
      await load();
    } finally {
      setSending(false);
    }
  };
  // A closed review cannot take a message: its session is archived and its
  // documents deleted. Picking it up starts a new review carrying this
  // conversation and the same documents, fetched again (2 Nov 2026).
  const doPickUp = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    setSendError("");
    try {
      const r = await axios.post(`${API}/reviews/${jobId}/pick-up`, {
        message: replyText,
        attached_files: acceptedAttachments.map((f) => f.filename),
      });
      if (r?.data?.started && r.data.job_id) {
        setReplyText("");
        setAttachedFiles([]);
        if (onOpen) onOpen(r.data.job_id);
        return;
      }
      setSendError(r?.data?.error || "The review could not be picked up.");
      if (r?.data?.job_id && onOpen) onOpen(r.data.job_id);
    } catch (e) {
      setSendError(e?.response?.data?.detail || "The review could not be picked up.");
    } finally {
      setSending(false);
    }
  };
  const doApprove = async (decision) => {
    await axios.post(`${API}/reviews/${jobId}/approve`, { decision });
    load();
  };
  const saveOutputToMatter = async (output) => {
    setOutputSaveState((prev) => ({ ...prev, [output.output_id]: "saving" }));
    try {
      const r = await axios.post(
        `${API}/reviews/${jobId}/save-output/${output.output_id}`,
        { filename: output.filename }
      );
      setOutputSaveState((prev) => ({ ...prev, [output.output_id]: r.data }));
    } catch (e) {
      setOutputSaveState((prev) => ({
        ...prev,
        [output.output_id]: { error: e?.response?.data?.detail || "Could not save this file." },
      }));
    }
  };
  const copyReply = () => {
    navigator.clipboard?.writeText(data.reply || "");
  };
  const downloadOutput = async (output) => {
    // A plain <a href> here always 401s (BM-H620 follow-up, 27 Sep 2026):
    // this page's identity travels as an X-Bonsai-User-Id header an axios
    // interceptor injects, which a raw browser navigation never carries,
    // and there is no cookie fallback. Fetch as a blob through axios
    // instead, same pattern already used for every other authenticated
    // download in this app (e.g. CompletionStatement.jsx).
    setOutputDownloadState((prev) => ({ ...prev, [output.output_id]: "downloading" }));
    try {
      const r = await axios.get(`${API}/reviews/${jobId}/outputs/${output.output_id}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", output.filename || "download");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setOutputDownloadState((prev) => ({ ...prev, [output.output_id]: null }));
    } catch (e) {
      const detail = await parseBlobError(e);
      setOutputDownloadState((prev) => ({ ...prev, [output.output_id]: { error: detail } }));
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200 mb-4">
        <ChevronLeft size={16} /> Back to Bonsai Agents
      </button>

      <div className="flex items-start justify-between mb-1">
        <h1 className="text-xl font-semibold text-slate-100">
          {data.matter_reference}{data.client_name ? `, ${data.client_name}` : ""}
        </h1>
        <StateBadge state={data.state} />
      </div>
      <p className="text-sm text-slate-400 mb-1">
        {data.agent_label} · {data.task_label} · started {fmtWhen(data.started_at)}{elapsed ? ` · ${elapsed}` : ""}
      </p>

      {data.state === "running" && (
        <div className="mb-4">
          <div className="h-1.5 w-full bg-white/[0.06] rounded-full overflow-hidden">
            <div
              className="h-full bg-teal/60 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, (elapsedMins / REVIEW_MAX_TURN_MINUTES) * 100)}%` }}
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            {elapsedMins} of up to {REVIEW_MAX_TURN_MINUTES} min before a stalled review is auto-stopped
            {" — "}most finish well before that; this isn't a completion estimate, just how close to that ceiling this one is.
          </p>
        </div>
      )}

      <div className="text-xs text-slate-500 mb-6">
        What went in: {data.documents?.length ? data.documents.join(", ") : "no documents"}.
        {data.read_access ? " Read access to the matter folder and email was allowed." : " No read access was allowed."}
      </div>

      {/* time-recording-foundations — hub.mate_review_agent.close_review now
          logs a native time entry automatically when this review closes
          (native ledger only, no LawBillity write). */}
      <div className="mb-6 border border-dashed border-slate-700/50 rounded-lg p-3 flex items-center gap-2 text-xs text-slate-500">
        <Clock size={14} className="shrink-0" />
        {data.state === "closed"
          ? "Time for this review was logged automatically — edit or remove it from My Time."
          : "Time will be logged automatically when this review closes, as an editable draft in My Time."}
      </div>

      {data.pending_approvals?.length > 0 && (
        <div className="mb-6 border border-amber-500/30 bg-amber-500/[0.06] rounded-lg p-4">
          <div className="flex items-center gap-2 text-amber-400 font-medium text-sm mb-2">
            <AlertTriangle size={16} /> Needs you
          </div>
          {data.pending_approvals.map((a, i) => (
            <div key={i} className="text-sm text-slate-300 mb-2">
              Asking permission to run <span className="font-mono text-xs">{a.tool}</span> with{" "}
              <span className="font-mono text-xs">{JSON.stringify(a.input || {})}</span>
            </div>
          ))}
          <div className="flex gap-2 mt-2">
            <button onClick={() => doApprove("allow")} className="px-3 py-1.5 rounded-md bg-emerald-500/15 text-emerald-400 text-sm hover:bg-emerald-500/25">Allow</button>
            <button onClick={() => doApprove("deny")} className="px-3 py-1.5 rounded-md bg-status-red-dim/30 text-status-red text-sm hover:bg-status-red-dim/40">Deny</button>
          </div>
        </div>
      )}

      {data.state === "running" && (
        <div className="mb-6">
          {confirmStop ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-300">Are you sure?</span>
              <button onClick={doStop} className="px-3 py-1 rounded-md bg-status-red-dim/30 text-status-red text-xs">Stop review</button>
              <button onClick={() => setConfirmStop(false)} className="px-3 py-1 rounded-md text-slate-400 text-xs hover:bg-slate-800">Cancel</button>
            </div>
          ) : (
            <button onClick={() => setConfirmStop(true)} className="px-3 py-1.5 rounded-md border border-slate-700/50 text-slate-400 text-xs hover:bg-slate-800">
              Stop review
            </button>
          )}
        </div>
      )}

      <ReviewChat
        data={data}
        replyText={replyText}
        setReplyText={setReplyText}
        attachedFiles={attachedFiles}
        acceptedAttachments={acceptedAttachments}
        attaching={attaching}
        sending={sending}
        sendError={sendError}
        replyFileInputRef={replyFileInputRef}
        attachReplyFiles={attachReplyFiles}
        removeAttachedFile={removeAttachedFile}
        onSend={doReply}
        onPickUp={doPickUp}
        onClose={doClose}
        onOpen={onOpen}
      />

      <div className="mb-6">
        <h2 className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Activity</h2>
        <ul className="space-y-1 text-sm">
          {(data.activity || []).length === 0 && <li className="text-slate-500 text-xs">Nothing yet.</li>}
          {(data.activity || []).map((line, i) => (
            <li key={i} className={line.startsWith("[FAILED]") ? "text-status-red" : "text-slate-300"}>
              {line.replace("[FAILED] ", "")}
            </li>
          ))}
        </ul>
      </div>

      {(data.state === "ready" || data.state === "closed" || data.state === "failed") && (
        <>
          {data.state === "failed" && (
            <div className="mb-6 text-status-red text-sm">The review did not finish: {data.failed_reason}</div>
          )}
          {data.reply && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                  Draft for review and sign off by the supervising solicitor
                </h2>
                <button onClick={copyReply} className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200">
                  <Copy size={12} /> Copy
                </button>
              </div>
              <div className="bg-white/[0.02] border border-slate-700/50 rounded-lg p-4">
                <MarkdownDoc markdown={data.reply} className="text-sm" />
              </div>
            </div>
          )}

          {data.sources && (
            <div className="mb-6">
              <h2 className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Sources</h2>
              {data.sources.opened_none && (
                <div className="text-status-red text-xs mb-2 flex items-center gap-1">
                  <AlertTriangle size={12} /> This reply opened no documents at all.
                </div>
              )}
              <ul className="text-xs space-y-0.5">
                {data.sources.opened.map((s, i) => (
                  <li key={i} className="text-slate-400 flex items-center gap-1"><ExternalLink size={10} /> {s}</li>
                ))}
                {data.sources.failed_to_open.map((s, i) => (
                  <li key={`f-${i}`} className="text-status-red">Could not open: {s}</li>
                ))}
              </ul>
            </div>
          )}

          {data.output_files?.length > 0 && (
            <div className="mb-6">
              <h2 className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Files it produced</h2>
              <ul className="space-y-1">
                {data.output_files.map((o) => {
                  const state = outputSaveState[o.output_id];
                  const dlState = outputDownloadState[o.output_id];
                  return (
                    <li key={o.output_id} className="bg-white/[0.02] border border-slate-700/50 rounded px-3 py-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-200">{o.filename}</span>
                        <div className="flex items-center gap-3 shrink-0">
                          <button
                            onClick={() => downloadOutput(o)}
                            disabled={dlState === "downloading"}
                            className="text-teal text-xs hover:underline disabled:opacity-40"
                          >
                            {dlState === "downloading" ? "Downloading..." : "Download"}
                          </button>
                          {state?.ok ? (
                            state.web_url ? (
                              <a href={state.web_url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 text-xs hover:underline">
                                Saved to Agent Memos
                              </a>
                            ) : (
                              <span className="text-emerald-400 text-xs">Saved to Agent Memos</span>
                            )
                          ) : (
                            <button
                              onClick={() => saveOutputToMatter(o)}
                              disabled={state === "saving"}
                              className="text-teal text-xs hover:underline disabled:opacity-40"
                            >
                              {state === "saving" ? "Saving..." : "Save to Agent Memos"}
                            </button>
                          )}
                        </div>
                      </div>
                      {state?.error && (
                        <p className="text-status-red text-xs mt-1">{state.error}</p>
                      )}
                      {dlState?.error && (
                        <p className="text-status-red text-xs mt-1">{dlState.error}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

        </>
      )}
    </div>
  );
}
